import React, { useRef, useState } from 'react';
import { CheckSquare, ChevronDown, FileText, GripVertical, Image, Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import { LstService } from '../services/lstService';
import { LST_SWI_MODE_LABELS, type LstSwiChecklistItem, type LstSwiMode } from '../types';
import { SwiRichTextField } from './SwiRichTextField';

export interface SwiFormState {
  swi_mode: LstSwiMode;
  swi_text: string;
  swi_doc_url: string;
  swi_checklist: LstSwiChecklistItem[];
}

export const EMPTY_SWI: SwiFormState = {
  swi_mode: 'text',
  swi_text: '',
  swi_doc_url: '',
  swi_checklist: [],
};

const MODES: LstSwiMode[] = ['text', 'document', 'checklist'];

const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';

interface SwiConfigSectionProps {
  organizationId: string;
  templateId: string | null;
  value: SwiFormState;
  onChange: (patch: Partial<SwiFormState>) => void;
  /** Accordion fechado por padrão (cadastro compacto). */
  defaultCollapsed?: boolean;
}

/** Seção de configuração da Instrução de Trabalho Padrão (SWI) no cadastro de rotinas. */
export const SwiConfigSection: React.FC<SwiConfigSectionProps> = ({
  organizationId,
  templateId,
  value,
  onChange,
  defaultCollapsed = false,
}) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const setMode = (mode: LstSwiMode) => onChange({ swi_mode: mode });

  const addCheckItem = () => {
    onChange({
      swi_checklist: [
        ...value.swi_checklist,
        { id: `chk_${Date.now()}`, text: '' },
      ],
    });
  };

  const updateCheckItem = (id: string, text: string) => {
    onChange({
      swi_checklist: value.swi_checklist.map((item) =>
        item.id === id ? { ...item, text } : item,
      ),
    });
  };

  const removeCheckItem = (id: string) => {
    onChange({ swi_checklist: value.swi_checklist.filter((item) => item.id !== id) });
  };

  const handleFilePick = async (file: File | null) => {
    if (!file || !organizationId) return;
    setPendingFile(file);
    setUploading(true);
    const url = await LstService.uploadSwiDocument(
      organizationId,
      templateId ?? 'draft',
      file,
    );
    setUploading(false);
    if (url) {
      onChange({ swi_doc_url: url, swi_mode: 'document' });
      setPendingFile(null);
    }
  };

  const isImage = (url: string) => /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);

  return (
    <div className="col-span-2 mt-1 rounded-md border border-dashed border-slate-200 bg-white/60 dark:border-slate-700 dark:bg-slate-900/20">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left"
      >
        <h4 className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
          Instrução de Trabalho Padrão (SWI)
        </h4>
        <ChevronDown
          size={14}
          className={`shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-2.5 pb-2.5 pt-2 dark:border-slate-800">
      {/* Seletor de modo */}
      <div className="mb-2 inline-flex overflow-hidden rounded-[5px] border border-slate-200 dark:border-slate-700">
        {MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setMode(mode)}
            className={`px-2 py-1 text-[10px] font-semibold transition-colors ${
              value.swi_mode === mode
                ? 'bg-[#2F6FED] text-white'
                : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-[var(--bg-surface)] dark:text-slate-400'
            }`}
          >
            {LST_SWI_MODE_LABELS[mode]}
          </button>
        ))}
      </div>

      {/* Modo Texto */}
      {value.swi_mode === 'text' && (
        <div>
          <label className={labelCls}>Orientações passo a passo</label>
          <SwiRichTextField
            value={value.swi_text}
            onChange={(html) => onChange({ swi_text: html })}
          />
        </div>
      )}

      {/* Modo Documento */}
      {value.swi_mode === 'document' && (
        <div>
          <label className={labelCls}>POP visual (imagem ou PDF)</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            className="hidden"
            onChange={(e) => handleFilePick(e.target.files?.[0] ?? null)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? 'Enviando…' : 'Anexar POP'}
            </button>
            {pendingFile && !value.swi_doc_url && (
              <span className="text-[10px] text-slate-400">{pendingFile.name}</span>
            )}
            {value.swi_doc_url && (
              <>
                <a
                  href={value.swi_doc_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  <Image size={12} /> Ver documento
                </a>
                <button
                  type="button"
                  onClick={() => onChange({ swi_doc_url: '' })}
                  className="rounded p-0.5 text-slate-400 hover:text-red-500"
                  title="Remover documento"
                >
                  <X size={12} />
                </button>
              </>
            )}
          </div>
          {value.swi_doc_url && isImage(value.swi_doc_url) && (
            <img
              src={value.swi_doc_url}
              alt="POP visual"
              className="mt-2 max-h-28 rounded border border-slate-200 object-contain dark:border-slate-700"
            />
          )}
        </div>
      )}

      {/* Modo Questionário */}
      {value.swi_mode === 'checklist' && (
        <div>
          <label className={labelCls}>Sub-passos de verificação</label>
          <div className="space-y-1.5">
            {value.swi_checklist.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-1.5">
                <GripVertical size={12} className="shrink-0 text-slate-300" />
                <span className="w-5 shrink-0 text-center text-[10px] font-bold text-slate-400">{idx + 1}</span>
                <input
                  className="h-7 flex-1 rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
                  value={item.text}
                  onChange={(e) => updateCheckItem(item.id, e.target.value)}
                  placeholder="Ex.: Verificar EPIs da equipe"
                />
                <button
                  type="button"
                  onClick={() => removeCheckItem(item.id)}
                  className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addCheckItem}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            <Plus size={12} /> Adicionar passo
          </button>
        </div>
      )}

      {/* Indicadores de conteúdo configurado em outros modos */}
      <div className="mt-2 flex flex-wrap gap-2 text-[9px] text-slate-400">
        {value.swi_text && value.swi_mode !== 'text' && (
          <span className="inline-flex items-center gap-0.5"><FileText size={9} /> Texto salvo</span>
        )}
        {value.swi_doc_url && value.swi_mode !== 'document' && (
          <span className="inline-flex items-center gap-0.5"><Image size={9} /> POP anexado</span>
        )}
        {value.swi_checklist.length > 0 && value.swi_mode !== 'checklist' && (
          <span className="inline-flex items-center gap-0.5"><CheckSquare size={9} /> {value.swi_checklist.length} passo(s)</span>
        )}
      </div>
        </div>
      )}
    </div>
  );
};

export default SwiConfigSection;
